import { InjectionToken } from '@angular/core';
import { SelectedFile } from '../../../components/file-upload/file-upload.component';
import { Account } from '../../../domain/account.domain';
import { CashTransferAccount, FrequencyTypes } from '../cash/cash-transfer.domain';
import { Currency } from '../../../domain/market.domain';

export enum OfiActiveStep {
  CREATE = 'create',
  CONFIRM = 'confirm',
  REVIEW = 'review',
  RECURRING_FROM_ACCOUNT = 'fromAccount',
  TERMS_AND_CONDITIONS = 'termsAndConditions',
  RECURRING_OPTIONS = 'recurringOptions',
  RECURRING_REVIEW = 'recurringReview',
  RECURRING_CONFIRM = 'recurringConfirm'
}

export interface OfiTransfer {
  selectedToAccount: CashTransferAccount;
  recurringAccounts?: Array<Account>;
  ofiOBDCAccount?: Array<OfiOBDCAccount>;
  selectedFromAccount?: OfiOBDCAccount;
  isRecurring?: boolean;
  transferToken?: string;
  externalTransferSite?: string;
  referenceId?: string;
  transferAmount?: number;
  externalInstitutionName?: string;
  externalTransitNumber?: string;
  externalInstitutionNumber?: string;
  externalAccountNumber?: string;
  frequencyType?: FrequencyTypes;
  voidCheque?: Array<SelectedFile>;
  startDate?: Date;
  contributionType?: string;
  openBankingInitiated?: boolean;
  csSessionId?: string;
  hostedLinkUrl?: string;
  externalAccountReady?: boolean;
  currency?: Currency;
  email?: string;
}

export interface OfiTransferRequest {
  selectedToAccount: string;
  transferAmount: number;
  selectedToAccountSessionKey?: string;
  transferToken?: string;
  email?: string;
  externalInstitutionName?: string;
  externalTransitNumber?: string;
  externalInstitutionNumber?: string;
  externalAccountNumber?: string;
  externalCurrency?: string;
  currency?: Currency;
  frequencyType?: FrequencyTypes;
  isRecurring?: boolean;
  voidCheque?: Array<SelectedFile>;
  startDate?: Date;
  contributionType?: string;
  csSessionId?: string;
  externalAccountName?: string;
  externalPayorNames?: Array<string>;
}

export interface OfiTransferResponse {
  interacUrl: string;
  referenceId: string;
}

export interface OfiEligibilitiesResponse {
  eligibleCADAccount: boolean;
  eligibleUSDAccount: boolean;
}

// Backend Responses
export interface OfiAccountsResponse {
  fromAccounts: Array<CashTransferAccount>;
  ineligibleFromAccounts?: Array<CashTransferAccount>;
  transferToken?: string;
}

export interface OfiOpenBankingConfiguration {
  openBankingDataUserInterfaceUrl: string;
}

export interface OfiOBDCAccount {
  externalInstitutionNumber: string;
  externalTransitNumber: string;
  externalAccountNumber: string;
  externalInstitutionName: string;
  externalPayorNames: Array<string>;
  accountId: string;
  name: string;
  isoCurrencyCode: string;
  verificationStatus: OfiAccountVerificationStatus;
  verificationDetail: string;
  duplicateFlag: boolean
}

export interface OfiOBDCAccountResponse {
  ofiAccounts: Array<OfiOBDCAccount>;
}

export interface OfiAccountUnlinkStatus {
  accountId: string;
  status: string;
  statusCode: string;
  httpStatus: number;
  reason: string;
}

export interface OfiUnlinkAccountsResponse {
  accountStatuses: Array<OfiAccountUnlinkStatus>;
  message: string;
  traceId: string;
  statusCode: string;
  status: string;
}

export interface EftRailTransferResponse {
  referenceId: string;
}

export interface AccountDepositProfileResponse {
  profile: Array<AccountDepositProfile>;
}

export interface AccountDepositProfile {
  agreementNum: string;
  allowableEftAmt: number;
}

export interface OfiRecurringExternalAccount {
  externalInstitutionNumber: string;
  externalTransitNumber: string;
  externalAccountNumber: string;
  externalInstitutionName: string;
}

export interface OfiRecurringExternalAccounts {
  ofiAccounts: Array<OfiRecurringExternalAccount>;
}

export interface OfiRecurringTransferDisclosure {
  ofiDisclosure: string;
}

export interface OfiRecurringTransferAgreement {
  csSessionId: string;
  hostedLinkUrl: string;
}

export interface OfiOpenBankingData {
  csSessionId: string;
  aggregatorId: string;
  samlToken: string;
}

export interface OfiOpenBankingDropdown {
  fromAccounts: Array<OfiOBDCAccount>;
  eligibleToAccounts: Array<CashTransferAccount>;
  ineligibleToAccounts: Array<CashTransferAccount>;
}

export interface OfiObduiAgginElement extends HTMLElement {
  keepaliveIntervalMs: number;

  startAggin(aggId: string, csSessionID: string, saml: string): void;
}

export interface OfiObduiResult {
  event: OfiObduiEvent;
  csSessionId: string;
}

export enum OfiObduiEvent {
  OBDUI_SUCCESS = 'obdui-success',
  OBDUI_ERROR = 'obdui-error',
  OBDUI_EXIT = 'obdui-exit',
  OBDUI_KEEPALIVE = 'obdui-keepalive'
}

export enum OfiAccountVerificationStatus {
  VERIFIED = 'VERIFIED',
  PENDING = 'PENDING',
  FAILED = 'FAILED'
}

export enum OfiTransferErrorCode {
  SESSION_ERROR = 'SESSION_ERROR',
  VALIDATION_ERROR = 'VALIDATION_ERROR'
}

export type OfiErrorAccountType = 'failed' | 'duplicate';

export const OFI_OPEN_BANKING_CONFIGURATION = new InjectionToken<OfiOpenBankingConfiguration>('ofiOpenBankingConfiguration');
